import type { QuotaMeResponse } from "../../modules/tutor/api/quota.client";
import { labelForTutorFeature } from "../../modules/tutor/api/quotaLabels";
import { ROUTES } from "../../utils/constants";
import { resolveExperienceQuotaDisplay } from "../../tutor/utils/tutorLiveQuotaUtils";

export type SidebarProgressScope =
  | { mode: "feature"; featureName: string; caption: string }
  | { mode: "pool"; caption: string };

export type TodayMinutesProgress = {
  used: number;
  limit: number | null;
  percent: number;
};

/** Monthly usage progress for Quota Runtime V2 student sidebar. */
export function resolveMonthlyUsageProgress(q: {
  monthly_used_minutes?: number | null;
  monthly_budget_minutes?: number | null;
  monthly_remaining_minutes?: number | null;
  monthly_used_seconds?: number | null;
  monthly_allocated_seconds?: number | null;
  monthly_remaining_seconds?: number | null;
} | null): TodayMinutesProgress {
  if (!q) return { used: 0, limit: null, percent: 0 };
  const used =
    q.monthly_used_minutes != null
      ? Math.max(0, Math.floor(q.monthly_used_minutes))
      : Math.floor((q.monthly_used_seconds ?? 0) / 60);
  const limit =
    q.monthly_budget_minutes != null
      ? Math.max(0, Math.floor(q.monthly_budget_minutes))
      : q.monthly_allocated_seconds != null
        ? Math.max(0, Math.floor(q.monthly_allocated_seconds / 60))
        : null;
  const percent =
    limit != null && limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  return { used, limit, percent };
}

export function resolveSidebarProgressScope(pathname: string): SidebarProgressScope {
  if (pathname.includes(ROUTES.STUDENT_SCENARIO_CONVERSATION)) {
    return {
      mode: "feature",
      featureName: "scenario_conversation_tutor",
      caption: labelForTutorFeature("scenario_conversation_tutor"),
    };
  }
  if (pathname.includes(ROUTES.STUDENT_COMMUNICATION_TUTOR)) {
    return {
      mode: "feature",
      featureName: "communication_tutor",
      caption: labelForTutorFeature("communication_tutor"),
    };
  }
  if (pathname.includes(ROUTES.STUDENT_PICTURE_TALK)) {
    return {
      mode: "feature",
      featureName: "picture_talk_tutor",
      caption: labelForTutorFeature("picture_talk_tutor"),
    };
  }
  if (pathname.includes(ROUTES.STUDENT_TAMIL_TUTOR)) {
    return {
      mode: "feature",
      featureName: "ai_lesson_tutor",
      caption: labelForTutorFeature("ai_lesson_tutor"),
    };
  }
  return { mode: "pool", caption: "All live tutors" };
}

export function resolveFeatureTodayMinutes(q: QuotaMeResponse | null): TodayMinutesProgress {
  if (!q || q.feature_disabled) {
    return { used: 0, limit: null, percent: 0 };
  }

  const display = resolveExperienceQuotaDisplay(q, 0);
  if (display.allocatedSeconds != null) {
    const used = Math.floor(display.usedSeconds / 60);
    const limit = Math.max(0, Math.floor(display.allocatedSeconds / 60));
    const percent =
      limit > 0 ? Math.min(100, Math.round((display.usedSeconds / display.allocatedSeconds) * 100)) : 0;
    return { used, limit, percent };
  }

  const pool = q.shared_pool;
  if (pool?.daily_limit_minutes != null) {
    const used = Math.max(0, pool.daily_used_minutes ?? 0);
    const limit = Math.max(0, pool.daily_limit_minutes);
    const percent = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
    return { used, limit, percent };
  }

  const used = Math.floor((q.daily_used_seconds ?? 0) / 60);
  const limit =
    q.daily_allocated_seconds != null ? Math.max(0, Math.floor(q.daily_allocated_seconds / 60)) : null;
  const percent = limit != null && limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  return { used, limit, percent };
}

/** Combined live AI pool usage (all tutor experiences). */
export function resolvePooledTodayMinutes(q: QuotaMeResponse | null): TodayMinutesProgress {
  if (!q) {
    return { used: 0, limit: null, percent: 0 };
  }

  const allocSec = q.live_pool_daily_allocated_seconds;
  if (allocSec != null && allocSec > 0) {
    const usedSec = Math.max(0, q.live_pool_daily_used_seconds ?? 0);
    const used = Math.floor(usedSec / 60);
    const limit = Math.max(0, Math.floor(allocSec / 60));
    const percent = Math.min(100, Math.round((usedSec / allocSec) * 100));
    return { used, limit, percent };
  }

  const pool = q.shared_pool;
  if (pool?.daily_limit_minutes != null) {
    const used = Math.max(0, pool.daily_used_minutes ?? 0);
    const limit = Math.max(0, pool.daily_limit_minutes);
    const percent = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
    return { used, limit, percent };
  }

  return resolveFeatureTodayMinutes(q);
}

export function quotaFeatureNameForScope(scope: SidebarProgressScope): string {
  return scope.mode === "pool" ? "live_ai_tutor" : scope.featureName;
}
